import type { QualityTier } from '@cinematic-engine/core';
import { useRef, useState } from 'react';
import { pickMimeType, resolveExportSettings, runExport } from '../../media/export-manager';
import type { ExportFormat } from '../../media/export-manager';
import { buildDirectedShots } from '../../media/render-engine';
import {
  type WebExportPayload,
  buildStandaloneHtml,
  downloadHtml,
  exportProjectFolder,
  previewHtml,
} from '../../media/web-export';
import { exportFileName, timelineEndSeconds, useStudio } from '../../store/studio-store';

export function RenderPanel() {
  const project = useStudio((s) => s.project);
  const assets = useStudio((s) => s.assets);
  const tracks = useStudio((s) => s.tracks);
  const exportState = useStudio((s) => s.exportState);
  const setExportState = useStudio((s) => s.setExportState);
  const cancelExport = useStudio((s) => s.cancelExport);

  const [qualityTier, setQualityTier] = useState<QualityTier>('PREVIEW');
  const [exportFormat, setExportFormat] = useState<ExportFormat>('webm');
  const cancelledRef = useRef(false);

  const duration = timelineEndSeconds(useStudio.getState());
  const projectName = project?.metadata.title ?? 'cinematic';

  function append(line: string) {
    setExportState({ message: line });
  }

  async function handleRender() {
    if (exportState.status === 'running') return;
    if (assets.length === 0) {
      append('No assets imported. Import media and place it on the timeline first.');
      return;
    }
    if (duration <= 0) {
      append('Timeline is empty. Add clips to the timeline before exporting.');
      return;
    }

    cancelledRef.current = false;
    const sourceFps = project?.format.fps ?? 24;
    const sourceWidth = project?.format.width ?? 1920;
    const sourceHeight = project?.format.height ?? 1080;
    const settings = resolveExportSettings(qualityTier, sourceWidth, sourceHeight, sourceFps);

    setExportState({ status: 'running', progress: 0, message: 'Preparing export…', blobUrl: null });
    try {
      const mimeType = pickMimeType(exportFormat);
      append(
        `Encoding as ${mimeType} at ${settings.width}×${settings.height} @ ${settings.fps}fps`
      );
      const ok = await runExport({
        assets: new Map(assets.map((a) => [a.id, a])),
        tracks,
        duration,
        settings,
        format: exportFormat,
        fileName: exportFileName(projectName, exportFormat),
        onProgress: (progress, message) => setExportState({ progress, message }),
        onResult: (blobUrl) => {
          setExportState({ status: 'done', progress: 1, message: 'Export complete', blobUrl });
          const a = document.createElement('a');
          a.href = blobUrl;
          a.download = exportFileName(projectName, exportFormat);
          a.click();
        },
        isCancelled: () => cancelledRef.current,
      });
      if (!ok) {
        setExportState({ status: 'cancelled', message: 'Export cancelled' });
      }
    } catch (error) {
      console.error(error);
      setExportState({
        status: 'error',
        message: error instanceof Error ? error.message : 'Export failed',
      });
    }
  }

  function handleCancel() {
    cancelledRef.current = true;
    cancelExport();
  }

  function buildWebExport(): WebExportPayload | null {
    if (!project) return null;
    const modelAssets = assets.filter((a) => a.kind === 'glb' || a.kind === 'gltf');
    const models = modelAssets
      .filter((asset) => asset.dataUrl)
      .map((asset) => ({ assetId: asset.id, name: asset.name, dataUrl: asset.dataUrl as string }));

    const modelClipAt = (start: number, end: number): string | undefined => {
      for (const track of tracks) {
        for (const clip of track.clips) {
          if (clip.start < end && start < clip.start + clip.duration) {
            const asset = modelAssets.find((a) => a.id === clip.assetId);
            if (asset?.dataUrl) return asset.id;
          }
        }
      }
      return undefined;
    };

    const shots = buildDirectedShots(project.scenes).map((shot) => ({
      start: shot.start,
      duration: shot.duration,
      title: shot.title,
      subtitle: shot.subtitle,
      color: shot.color,
      modelAssetId: modelClipAt(shot.start, shot.start + shot.duration),
    }));
    return {
      title: project.metadata.title,
      width: project.format.width,
      height: project.format.height,
      fps: project.format.fps,
      shots,
      models,
    };
  }

  function handleDownloadWeb() {
    const payload = buildWebExport();
    if (!payload) return;
    const html = buildStandaloneHtml(payload);
    downloadHtml(html, `${exportFileName(projectName, 'html').replace(/\.html$/, '')}.html`);
    append('HTML+Tree.js export gerado e baixado.');
  }

  function handlePreviewWeb() {
    const payload = buildWebExport();
    if (!payload) return;
    previewHtml(buildStandaloneHtml(payload));
    append('Preview do HTML+Tree.js aberto em nova aba.');
  }

  async function handleExportFolder() {
    const payload = buildWebExport();
    if (!payload) return;
    try {
      const result = await exportProjectFolder(payload);
      if (result.cancelled) {
        append('Exportação para pasta cancelada.');
      } else {
        append(`Pasta exportada: ${result.written} arquivo(s) (HTML + assets separados).`);
      }
    } catch (error) {
      append(error instanceof Error ? error.message : 'Falha ao exportar pasta');
    }
  }

  const running = exportState.status === 'running';

  return (
    <div className="panel">
      <div className="panel-header">
        <h2>Render</h2>
      </div>

      <section className="panel-section">
        <h3>Export Settings</h3>
        <div className="export-form">
          <label>
            <span>Quality</span>
            <select
              value={qualityTier}
              onChange={(e) => setQualityTier(e.target.value as QualityTier)}
            >
              <option value="DRAFT">DRAFT</option>
              <option value="PREVIEW">PREVIEW</option>
              <option value="HIGH">HIGH</option>
              <option value="MASTER">MASTER</option>
            </select>
          </label>
          <label>
            <span>Format</span>
            <select
              value={exportFormat}
              onChange={(e) => setExportFormat(e.target.value as ExportFormat)}
            >
              <option value="webm">WebM</option>
              <option value="mp4">MP4</option>
            </select>
          </label>
          <div className="export-readout">
            <div>Resolution</div>
            <div>
              {project ? `${project.format.width} × ${project.format.height}` : '1920 × 1080'}
            </div>
            <div>FPS</div>
            <div>{project?.format.fps ?? 24}</div>
            <div>Duration</div>
            <div>{duration.toFixed(2)}s</div>
            <div>Clips</div>
            <div>{tracks.reduce((acc, t) => acc + t.clips.length, 0)}</div>
          </div>
        </div>
        <div className="action-row">
          {!running ? (
            <button type="button" className="primary-btn" onClick={() => void handleRender()}>
              Start Render
            </button>
          ) : (
            <button type="button" className="ghost-btn danger-btn" onClick={handleCancel}>
              Cancel
            </button>
          )}
        </div>
      </section>

      {running && (
        <section className="panel-section">
          <h3>Progress</h3>
          <div className="progress-track">
            <div
              className="progress-fill"
              style={{ width: `${Math.round(exportState.progress * 100)}%` }}
            />
          </div>
          <div className="progress-label">
            {Math.round(exportState.progress * 100)}% · {exportState.message}
          </div>
        </section>
      )}

      {exportState.status === 'done' && exportState.blobUrl && (
        <section className="panel-section">
          <h3>Done</h3>
          <div className="action-row">
            <a
              className="primary-btn download-btn"
              href={exportState.blobUrl}
              download={exportFileName(projectName, exportFormat)}
            >
              Download {exportFormat.toUpperCase()}
            </a>
          </div>
        </section>
      )}

      <section className="panel-section">
        <h3>HTML + Tree.js Export</h3>
        <p className="hint">
          Gera um arquivo HTML único com Three.js (via CDN) que reproduz o vídeo dirigido — câmera
          animada por plano, fundo por emoção, tipografia e controles (play, seek, setas). Roda
          offline após carregar a primeira vez. Para modelos 3D grandes, use a exportação em pasta
          (assets separados, sem base64).
        </p>
        <div className="action-row">
          <button
            type="button"
            className="primary-btn"
            onClick={handleDownloadWeb}
            disabled={!project || running}
          >
            ⬇ Exportar HTML+Tree.js
          </button>
          <button
            type="button"
            className="ghost-btn"
            onClick={handlePreviewWeb}
            disabled={!project || running}
          >
            Preview
          </button>
          <button
            type="button"
            className="ghost-btn"
            onClick={() => void handleExportFolder()}
            disabled={!project || running}
          >
            📁 Exportar como pasta
          </button>
        </div>
      </section>

      {exportState.message && exportState.status !== 'done' && (
        <section className="panel-section">
          <h3>Render Log</h3>
          <pre className="render-log">{exportState.message}</pre>
        </section>
      )}
    </div>
  );
}
